import { memo, useCallback } from 'react';

import { CheckIcon } from '@air/design-icons';

import { type BrandColorEntry } from '~/components/ShareModal/shareLinkDesignResolution';

export type ShareDesignColorRowBrandOptionProps = {
  entry: BrandColorEntry;
  /** True when the row's current hex matches this entry — renders a trailing check. */
  isSelected: boolean;
  onSelect: (entry: BrandColorEntry) => void;
  /** data-testid for the option button — the parent namespaces it per color role. */
  testId: string;
};

/** One brand-kit color swatch in a color row's popover: a color circle plus the value's label. */
export const ShareDesignColorRowBrandOption = memo(
  ({ entry, isSelected, onSelect, testId }: ShareDesignColorRowBrandOptionProps) => {
    const handleClick = useCallback(() => {
      onSelect(entry);
    }, [entry, onSelect]);

    return (
      <button
        className="flex h-8 items-center gap-2 rounded-4 px-2 text-left ads:text-primary text-14 text-gray-12 ads:hover:bg-hover-subtle hover:bg-gray-3"
        data-id={entry.id}
        data-testid={testId}
        onClick={handleClick}
        type="button"
      >
        <span
          className="size-3.5 shrink-0 rounded-full border border-black/10"
          style={{ backgroundColor: entry.hex }}
        />
        <span className="min-w-0 flex-1 truncate">{entry.label}</span>
        {isSelected && <CheckIcon size="sm" className="shrink-0 ads:text-icon-primary text-blue-9" />}
      </button>
    );
  },
);

ShareDesignColorRowBrandOption.displayName = 'ShareDesignColorRowBrandOption';
